import { MathUtils, Path } from "three";
import { type ZodTypeAny, z } from "zod";
import {
	type ThreeInputLinesType,
	ThreeShapeSideTypes,
	type ThreeSidePathsType,
} from "../../../../../types/three";
import type { DimensionProperties } from "../../../../../types/workpiece";
import Oval from "../Oval";

export default class Flat extends Oval {
	public static code = "plate_oval_flat";

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema();

		return inputs.superRefine((data, context) => {
			if (data.width > data.length) {
				context.addIssue({
					code: z.ZodIssueCode.too_small,
					type: "number",
					minimum: data.width,
					inclusive: true,
					path: ["length"],
				});
			}
		});
	}

	protected calculateOutlineLength(length: number, width: number): number {
		return (length - width) * 2 + width * Math.PI;
	}

	public getThreeSidePaths(props: DimensionProperties["plate_oval_flat"]): ThreeSidePathsType[] {
		const [length, width] = [props.length / 1000, props.width / 1000];

		return [
			{
				path: new Path().moveTo(width / 2, 0).lineTo(length - width / 2, 0),
				type: ThreeShapeSideTypes.side,
				group: "group1",
				code: "length1",
			},
			{
				path: new Path()
					.moveTo(length - width / 2, 0)
					.arc(0, width / 2, width / 2, MathUtils.degToRad(270), MathUtils.degToRad(90), false),
				type: ThreeShapeSideTypes.side,
				group: "group1",
				code: "width2",
			},
			{
				path: new Path().moveTo(length - width / 2, width).lineTo(width / 2, width),
				type: ThreeShapeSideTypes.side,
				group: "group1",
				code: "length2",
			},
			{
				path: new Path()
					.moveTo(width / 2, width / 2)
					.arc(0, 0, width / 2, MathUtils.degToRad(1 * 90), MathUtils.degToRad(-1 * 90), false),
				type: ThreeShapeSideTypes.side,
				group: "group1",
				code: "width2",
			},
		];
	}

	public getThreeInputLines(props: DimensionProperties["plate_oval_flat"]): ThreeInputLinesType[] {
		const [length, width] = [props.length / 1000, props.width / 1000];

		return [
			{
				dimension: props.length,
				path: new Path().moveTo(0, 0).lineTo(length, 0),
				type: ThreeShapeSideTypes.side,
				code: "length",
			},
			{
				dimension: props.width,
				path: new Path().moveTo(0, 0).lineTo(0, width),
				type: ThreeShapeSideTypes.side,
				code: "width",
			},
		];
	}

	public getDimensions(props: DimensionProperties["plate_oval_flat"]) {
		return {
			width: props.width ? props.width : props.length ? props.length / 2 : 1000,
			length: props.length ? props.length : props.width ? props.width * 2 : 2000,
		};
	}
}
